import axios from 'axios';

// Ensure API_URL has /api in it
let API_URL = import.meta.env.VITE_API_URL 
  ? `${import.meta.env.VITE_API_URL}`
  : 'http://localhost:3001';
// If API_URL is just '/api', don't duplicate it
if (API_URL === '/api') {
  API_URL = '';
}

/**
 * Budget Service
 * 
 * Handles all budget-related operations and data fetching
 */
const budgetService = {
  /**
   * Get authentication header with JWT token
   * @returns {Object} Headers object with Authorization
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  },

  /**
   * Get all budgets for the current user
   * @returns {Promise} Promise with budgets data
   */
  async getAllBudgets() {
    try {
      const response = await axios.get(`${API_URL}/api/budget`, this.getAuthHeader());
      return response.data;
    } catch (error) {
      console.error('Error fetching budgets:', error);
      throw error;
    }
  },
  
  /**
   * Get a budget by its ID
   * @param {string} budgetId - The budget ID to retrieve
   * @returns {Promise} Promise with budget data
   */
  async getBudgetById(budgetId) {
    try {
      const response = await axios.get(`${API_URL}/api/budget/${budgetId}`, this.getAuthHeader());
      return response.data;
    } catch (error) {
      console.error(`Error fetching budget ${budgetId}:`, error);
      throw error;
    }
  },
  
  /**
   * Create a new budget
   * @param {Object} budgetData - The budget data to create
   * @returns {Promise} Promise with created budget data
   */
  async createBudget(budgetData) {
    try {
      const response = await axios.post(`${API_URL}/api/budget`, budgetData, this.getAuthHeader());
      return response.data;
    } catch (error) {
      console.error('Error creating budget:', error);
      throw error;
    }
  },
  
  /**
   * Update a budget
   * @param {string} budgetId - The budget ID to update
   * @param {Object} budgetData - The updated budget data
   * @returns {Promise} Promise with updated budget data
   */
  async updateBudget(budgetId, budgetData) {
    try {
      const response = await axios.put(
        `${API_URL}/api/budget/${budgetId}`, 
        budgetData, 
        this.getAuthHeader()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating budget ${budgetId}:`, error);
      throw error;
    }
  },
  
  /**
   * Delete a budget
   * @param {string} budgetId - The budget ID to delete
   * @returns {Promise} Promise with deletion result
   */
  async deleteBudget(budgetId) {
    try {
      const response = await axios.delete(
        `${API_URL}/api/budget/${budgetId}`, 
        this.getAuthHeader()
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting budget ${budgetId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get budget statistics for dashboard
   * @returns {Promise} Promise with budget stats data
   */
  async getBudgetStatistics() {
    try {
      // Either use a dedicated endpoint or calculate from existing data
      let budgets = await this.getAllBudgets();
      if (!Array.isArray(budgets)) budgets = [];
      // Find the primary/active budget
      const activeBudget = budgets.find(b => b.isPrimary || b.isActive) || budgets[0];
      if (!activeBudget) {
        return {
          totalBudgeted: 0,
          spent: 0,
          remaining: 0,
          savingsRate: 0,
          budgetCount: 0,
          categoryCoverage: 0,
          utilization: 0
        };
      }
      // Always treat categories as an array
      const categories = Array.isArray(activeBudget.categories) ? activeBudget.categories : [];
      // Calculate total budgeted amount (sum of all category budgets)
      const totalBudgeted = categories.reduce(
        (sum, category) => sum + (category.budgeted || 0), 0
      ) || 0;
      // Calculate spent amount (sum of all category actuals)
      const spent = categories.reduce(
        (sum, category) => sum + (category.actual || 0), 0
      ) || 0;
      // Calculate remaining amount
      const remaining = totalBudgeted - spent;
      // Calculate savings rate as a percentage of income
      const totalIncome = Array.isArray(activeBudget.income)
        ? activeBudget.income.reduce((sum, item) => sum + (item.amount || 0), 0)
        : 0;
      const savingsCategory = categories.find(
        cat => cat.name && cat.name.toLowerCase().includes('saving')
      );
      const savingsAmount = savingsCategory?.budgeted || 0;
      const savingsRate = totalIncome > 0 
        ? (savingsAmount / totalIncome) * 100 
        : 0;
      // Calculate budget utilization
      const utilization = totalBudgeted > 0 
        ? (spent / totalBudgeted) * 100 
        : 0;
      // Count number of budgets
      const budgetCount = budgets.length;
      // Calculate category coverage (percentage of categories that have a budget)
      const categoriesWithBudget = categories.filter(
        cat => (cat.budgeted || 0) > 0
      ).length || 0;
      const totalCategories = categories.length || 1;
      const categoryCoverage = (categoriesWithBudget / totalCategories) * 100;
      return {
        totalBudgeted,
        spent,
        remaining,
        savingsRate,
        budgetCount,
        categoryCoverage,
        utilization
      };
    } catch (error) {
      console.error('Error fetching budget statistics:', error);
      // Return default values instead of throwing
      return {
        totalBudgeted: 0,
        spent: 0,
        remaining: 0,
        savingsRate: 0,
        budgetCount: 0,
        categoryCoverage: 0,
        utilization: 0
      };
    }
  },

  /**
   * Get actual spending for a budget
   * @param {string} budgetId - The budget ID
   * @returns {Promise} Promise with actual spending data
   */
  async getActualSpending(budgetId) {
    try {
      const response = await axios.get(
        `${API_URL}/api/budget/${budgetId}/actual`, 
        this.getAuthHeader()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching actual spending for budget ${budgetId}:`, error);
      throw error;
    }
  },
  
  /**
   * Clone a budget (create a scenario)
   * @param {string} budgetId - The budget ID to clone
   * @param {Object} options - Options for the cloned budget
   * @returns {Promise} Promise with cloned budget data
   */
  async cloneBudget(budgetId, options = {}) {
    try {
      const response = await axios.post(
        `${API_URL}/api/budget/${budgetId}/clone`,
        options,
        this.getAuthHeader()
      );
      return response.data;
    } catch (error) {
      console.error(`Error cloning budget ${budgetId}:`, error);
      throw error;
    }
  },
  
  /**
   * Compare two budgets
   * @param {string} baseBudgetId - The base budget ID
   * @param {string} comparisonBudgetId - The comparison budget ID
   * @returns {Object} Comparison data
   */
  async compareBudgets(baseBudgetId, comparisonBudgetId) {
    try {
      const [baseBudget, comparisonBudget] = await Promise.all([
        this.getBudgetById(baseBudgetId),
        this.getBudgetById(comparisonBudgetId)
      ]);
      
      return {
        base: baseBudget,
        comparison: comparisonBudget
      };
    } catch (error) {
      console.error(`Error comparing budgets ${baseBudgetId} and ${comparisonBudgetId}:`, error);
      throw error;
    }
  }
};

export default budgetService; 